import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../utils/AuthContext';
import { useUser } from '../utils/UserContext';
import { AcceptDriverInviteRequest, Entity } from '../types/apiTypes';
import useApi from '../utils/AxiosWrapper';
import { useScreenView } from '../utils/ScreenViewContext';
import { HttpStatusCode } from 'axios';
import toast from 'react-hot-toast';
import {
  safeSessionGetItem,
  safeSessionSetItem,
  safeSetItem,
} from '../shared/safeStorage';

function DriverSignup() {
  const { setUserRecord, setUserRole, userId, userRecord } = useUser();
  const { isAuthenticated, user, loginWithRedirect, logout } = useAuth();
  const { isLoading } = useAuth0();
  const { callApi, callProtectedApi } = useApi();
  const [loading, setLoading] = useState(true);
  const { screenView } = useScreenView();
  const [driverInviteVerified, setDriverInviteVerified] = useState(false);
  // Accept failed on a non-201 response (or network error). callProtectedApi
  // never throws and never returns a usable record in that case, so we surface
  // the failure here instead of leaving the driver on a perpetual spinner.
  const [acceptFailed, setAcceptFailed] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [inviteId, setInviteId] = useState<string>('');
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const tach_token = searchParams.get('tach_token') || '';
  const navigate = useNavigate();
  // Guards against duplicate accept POSTs: the accept effect can re-fire as
  // Auth0 callback state churns (isLoading toggling, sub/inviteId resolving).
  // A second POST against a non-idempotent backend could 409/500 and trip the
  // email-mismatch logout for the driver we just created.
  const acceptInProgress = useRef(false);

  const verifyDriverInvite = async () => {
    setLoading(true);
    try {
      const response = await callApi(
        `/driverInvites/verify`,
        'post',
        { tach_token },
        'Error occurred while verifying driver invite.'
      );
      const data = response?.data?.data;
      const inviteId = data?.invite_id;
      const email = data?.email;

      if (
        response?.status === HttpStatusCode.Ok &&
        inviteId &&
        inviteId?.length > 0
      ) {
        setDriverInviteVerified(true);
        setInviteId(inviteId);
        // Default to this route rather than concatenating onto the null a
        // blocked/empty store returns — that produced the literal string
        // "null?driver_invite_id=…", which is truthy enough to pass as a
        // redirectPath while matching no route. `/driver-signup` is the same
        // value the post-verification effect below stores, so the two agree.
        let redirect = safeSessionGetItem('redirectPath') ?? '/driver-signup';
        if (!redirect.includes('?driver_invite_id')) {
          redirect = redirect + '?driver_invite_id=' + inviteId;
          safeSessionSetItem('redirectPath', redirect);
        }
        if (email) {
          setInviteEmail(email);
        }
      }
    } catch (e: any) {
      console.error('Error occurred while verifying driver invite: ', e);
    } finally {
      if (isAuthenticated || !inviteEmail) {
        setLoading(false);
      }
    }
  };

  const acceptDriverInvite = async (invite_id: string) => {
    if (acceptInProgress.current) {
      return;
    }
    acceptInProgress.current = true;
    try {
      // Critical: Validate that the authenticated user's email matches the invite email
      if (inviteEmail && user?.email) {
        if (inviteEmail.toLowerCase() !== user.email.toLowerCase()) {
          toast.error(
            'Email mismatch detected. Please contact support if you need assistance.'
          );
          setTimeout(() => {
            logout({
              logoutParams: {
                returnTo: window.location.origin + '/login',
              },
            });
          }, 3000);
          return;
        }
      }

      // Create the Driver row (and thus the durable driver role) as soon as
      // Auth0 returns — no phone here. The phone is sourced from the Auth0
      // profile server-side at accept (THT-653), so there's no phone UI or
      // post-accept gate. Creating the row first also closes the window where a
      // stray Customer could shadow the driver.
      const response = await callProtectedApi(
        `/driverInvites/accept`,
        'post',
        { invite_id, sub_id: user?.sub } as AcceptDriverInviteRequest,
        'Error occurred while creating your driver account.'
      );
      const driver = response?.data?.data?.driver;
      if (
        response?.status === HttpStatusCode.Created &&
        driver &&
        Object.entries(driver)?.length > 0
      ) {
        setUserRecord({ ...driver, userType: Entity.Driver });
        setUserRole(Entity.Driver);
        safeSetItem('tachUserRole', Entity.Driver);
        // Driver account is ready — land directly in the driver app.
        navigate('/dashboard/vendor-marketplace');
      } else {
        // Any non-201 outcome — a 409 (a duplicate Auth0 phone now sourced
        // server-side), a 4xx/5xx, or a null/network failure. callProtectedApi
        // never throws and never returns a driver here, so without this branch
        // the driver would be stranded on the "Creating your driver account..."
        // spinner with no feedback. Surface the error and reset the guard so a
        // reload can retry.
        console.error(
          'Driver invite accept failed: ',
          response?.status ?? 'no response'
        );
        toast.error(
          'We could not create your driver account. Please refresh to try again, or contact support if this keeps happening.'
        );
        acceptInProgress.current = false;
        setAcceptFailed(true);
      }
    } catch (e: any) {
      // Safety net for unexpected throws (e.g. logout/navigate failing).
      // callProtectedApi itself resolves rather than throwing, so the non-201
      // handling above is the primary error path. Reset the guard so a reload
      // can retry rather than stranding the driver.
      console.error('Error occurred while accepting driver invite: ', e);
      acceptInProgress.current = false;
      setAcceptFailed(true);
    }
  };

  useEffect(() => {
    // Call verifyDriverInvite immediately on component mount if tach_token exists
    if (tach_token && !isAuthenticated) {
      verifyDriverInvite();
    } else {
      if (
        isAuthenticated &&
        userId &&
        userRecord &&
        userRecord?.userType === Entity.Driver
      ) {
        // A returning driver lands directly in the driver app.
        navigate('/dashboard/vendor-marketplace');
      }
      // If no tach_token but we have a driver_invite_id in the URL, we're likely returning from Auth0
      const driverId =
        searchParams.get('driver_invite_id') ||
        safeSessionGetItem('driver_invite_id');
      if (driverId) {
        setInviteId(driverId);
        setDriverInviteVerified(true);
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tach_token, isAuthenticated]); // Only depend on tach_token

  useEffect(() => {
    // Handle Auth0 authentication and driver invite acceptance. The accept runs
    // as soon as Auth0 returns so the driver/role is created immediately; the
    // phone is sourced from the Auth0 profile server-side — no phone UI.
    if (isAuthenticated && !isLoading && driverInviteVerified) {
      const driverId =
        inviteId ||
        searchParams.get('driver_invite_id') ||
        safeSessionGetItem('driver_invite_id');

      if (driverId && user?.sub) {
        acceptDriverInvite(driverId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, driverInviteVerified, inviteId, user?.sub]);

  useEffect(() => {
    // Redirect to Auth0 signup when we have verified the invite and have an email
    if (driverInviteVerified && inviteEmail && !isAuthenticated && !isLoading) {
      safeSessionSetItem('driver_invite_id', inviteId);
      // Store the current path to return to after authentication
      safeSessionSetItem(
        'redirectPath',
        `/driver-signup?driver_invite_id=${inviteId}`
      );

      loginWithRedirect({
        authorizationParams: {
          screen_hint: 'signup',
          login_hint: inviteEmail,
          prompt: 'login',
          scope: 'openid profile email',
        },
        appState: {
          returnTo: `/driver-signup?driver_invite_id=${inviteId}`,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverInviteVerified, inviteEmail, isAuthenticated, isLoading, inviteId]);

  const loadingView = (message: string) => (
    <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
      <img
        alt="loading-icon"
        src="/Icon-v2.gif"
        className="flex h-44 w-44 justify-center"
      />
      <h2 className="text-[14px] text-secondary">{message}</h2>
    </div>
  );

  const errorView = (message: string) => (
    <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center px-6 text-center align-middle">
      <h2 className="text-[14px] text-secondary">{message}</h2>
    </div>
  );

  const renderContent = () => {
    if (acceptFailed) {
      return errorView(
        'We could not create your driver account. Please refresh to try again, or contact support@tachusa.com if this keeps happening.'
      );
    }
    if (loading) {
      return loadingView('Verifying driver invite link...');
    }
    if (driverInviteVerified && isAuthenticated && !isLoading) {
      return loadingView('Creating your driver account...');
    }
    return null;
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return <div>{renderContent()}</div>;
      default:
        return null;
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default DriverSignup;
